import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, Trash2, ShieldAlert } from "lucide-react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, AdminHeading, adminInput, Th, Td } from "@/components/admin/ui";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { GhostIcon } from "@/components/ghost/GhostIcon";
import { LeadConvertModal } from "@/components/admin/LeadConvertModal";

const TOGGLES = [
  ["enabled", "D (global)", "Master switch for the entire D assistant."],
  ["guest_enabled", "Guest D", "The guest-facing concierge in the portal & app."],
  ["admin_enabled", "Admin D", "The operations assistant in the Command Center."],
  ["public_enabled", "Public-site D", "Show the launcher on approved public pages."],
  ["history_enabled", "Conversation history", "Save conversations (subject to retention)."],
  ["drafting_enabled", "Drafting", "Allow D to draft guest messages & replies."],
  ["search_enabled", "Record search", "Allow D to search authorized records."],
  ["briefings_enabled", "Briefings", "Enable operations briefings."],
  ["suggested_actions_enabled", "Suggested actions", "Allow D to propose (not execute) actions."],
];

export default function AdminDSettings() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "general";
  const typeFilter = sp.get("type") || "";
  const followupOnly = sp.get("followup") === "1";
  const setTab = (v) => setSp((p) => { const n = new URLSearchParams(p); n.set("tab", v); if (v !== "public") { n.delete("type"); n.delete("followup"); } return n; });
  const clearInqFilter = () => setSp((p) => { const n = new URLSearchParams(p); n.delete("type"); n.delete("followup"); return n; });
  const [s, setS] = useState(null);
  const [logs, setLogs] = useState(null);
  const [faqs, setFaqs] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [nf, setNf] = useState({ question: "", answer: "", category: "General", published: true });
  const [convLead, setConvLead] = useState(null);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const loadPublic = () => {
    api.get("/ghost/faqs").then((r) => setFaqs(r.data)).catch(() => setFaqs([]));
    api.get("/ghost/public/analytics").then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null));
  };
  const load = () => { api.get("/ghost/settings").then((r) => setS(r.data)).catch(() => setS(false)); api.get("/ghost/admin/logs").then((r) => setLogs(r.data)).catch(() => setLogs([])); loadPublic(); };
  useEffect(() => { load(); }, []);

  const addFaq = async () => {
    if (!nf.question || !nf.answer) { toast.error("Question and answer are required."); return; }
    try { await api.post("/ghost/faqs", nf); toast.success("FAQ added."); setNf({ question: "", answer: "", category: "General", published: true }); loadPublic(); } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleFaq = async (f) => { try { await api.put(`/ghost/faqs/${f.id}`, { published: !f.published }); loadPublic(); } catch (e) { toast.error(apiErr(e)); } };
  const delFaq = async (id) => { if (!window.confirm("Delete this FAQ?")) return; try { await api.delete(`/ghost/faqs/${id}`); loadPublic(); } catch (e) { toast.error(apiErr(e)); } };

  const save = async () => { try { await api.put("/ghost/settings", s); toast.success("D settings saved."); } catch (e) { toast.error(apiErr(e)); } };
  const purge = async () => { if (!window.confirm("Delete all saved D conversations? This cannot be undone.")) return;
    try { const { data } = await api.post("/ghost/admin/purge-history"); toast.success(`Deleted ${data.deleted} conversation(s).`); } catch (e) { toast.error(apiErr(e)); } };

  if (s === null) return <Loading />;
  if (!s) return <p className="text-muted-foreground">Owner access required.</p>;

  return (
    <div className="max-w-4xl">
      <AdminHeading title="D Settings" subtitle="Owner-only controls for The D's private AI concierge.">
        <button onClick={save} className="btn-gold px-5 py-2.5 text-sm flex items-center gap-2" data-testid="ghost-settings-save"><Save size={15} /> Save</button>
      </AdminHeading>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5"><TabsTrigger value="general">General</TabsTrigger><TabsTrigger value="content">Content</TabsTrigger><TabsTrigger value="public" data-testid="ghost-public-tab">Public</TabsTrigger><TabsTrigger value="privacy">Privacy</TabsTrigger><TabsTrigger value="audit" data-testid="ghost-audit-tab">Activity</TabsTrigger></TabsList>

        <TabsContent value="general" className="space-y-6">
          <Panel title="Identity">
            <div className="flex items-center gap-4 mb-4">
              <GhostIcon size={54} />
              <div className="flex-1 grid sm:grid-cols-2 gap-3">
                <input className={adminInput} value={s.display_name} onChange={(e) => set("display_name", e.target.value)} placeholder="Display name" data-testid="ghost-display-name" />
                <input className={adminInput} value={s.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Subtitle" />
                <input className={adminInput} value={s.tone} onChange={(e) => set("tone", e.target.value)} placeholder="Tone" />
              </div>
            </div>
          </Panel>
          <Panel title="Feature Toggles">
            <div className="space-y-3">{TOGGLES.map(([k, label, desc]) => (
              <div key={k} className="flex items-center justify-between">
                <div><p className="text-sm">{label}</p><p className="text-xs text-muted-foreground">{desc}</p></div>
                <Switch checked={!!s[k]} onCheckedChange={(v) => set(k, v)} data-testid={`ghost-toggle-${k}`} />
              </div>
            ))}</div>
          </Panel>
        </TabsContent>

        <TabsContent value="content" className="space-y-6">
          <Panel title="Greetings">
            <div className="space-y-3">
              <textarea className={adminInput} rows={2} value={s.greeting} onChange={(e) => set("greeting", e.target.value)} placeholder="Guest greeting" />
              <textarea className={adminInput} rows={2} value={s.admin_greeting} onChange={(e) => set("admin_greeting", e.target.value)} placeholder="Admin greeting" />
            </div>
          </Panel>
          <Panel title="Quick Prompts (one per line)">
            <div className="grid sm:grid-cols-2 gap-3">
              <textarea className={adminInput} rows={5} value={(s.guest_quick_prompts || []).join("\n")} onChange={(e) => set("guest_quick_prompts", e.target.value.split("\n").filter(Boolean))} placeholder="Guest prompts" />
              <textarea className={adminInput} rows={5} value={(s.admin_quick_prompts || []).join("\n")} onChange={(e) => set("admin_quick_prompts", e.target.value.split("\n").filter(Boolean))} placeholder="Admin prompts" />
            </div>
          </Panel>
        </TabsContent>

        <TabsContent value="public" className="space-y-6">
          <Panel title="Public FAQ Knowledge">
            <p className="text-xs text-muted-foreground mb-3">Approved answers D may use for public visitors. Published FAQs feed the public concierge; D never uses private records for public visitors.</p>
            <div className="space-y-2 mb-4">
              <input className={adminInput} placeholder="Question" value={nf.question} onChange={(e) => setNf({ ...nf, question: e.target.value })} data-testid="faq-question" />
              <textarea className={adminInput} rows={2} placeholder="Approved answer" value={nf.answer} onChange={(e) => setNf({ ...nf, answer: e.target.value })} data-testid="faq-answer" />
              <div className="flex gap-2"><input className={adminInput + " w-40"} placeholder="Category" value={nf.category} onChange={(e) => setNf({ ...nf, category: e.target.value })} /><button onClick={addFaq} className="btn-gold px-4 py-2 text-sm" data-testid="faq-add">Add FAQ</button></div>
            </div>
            {faqs === null ? <Loading /> : faqs.length === 0 ? <p className="text-sm text-muted-foreground">No FAQs yet.</p> : (
              <div className="space-y-2">{faqs.map((f) => (
                <div key={f.id} className="flex justify-between items-start border-b border-white/5 py-2" data-testid={`faq-${f.id}`}>
                  <div className="flex-1 pr-3"><p className="text-sm">{f.question}</p><p className="text-xs text-muted-foreground">{f.answer}</p><span className="text-[10px] text-muted-foreground uppercase tracking-label">{f.category}{f.published ? "" : " · unpublished"}</span></div>
                  <div className="flex items-center gap-2"><Switch checked={f.published} onCheckedChange={() => toggleFaq(f)} data-testid={`faq-pub-${f.id}`} /><button onClick={() => delFaq(f.id)} className="text-red-300" aria-label="Delete FAQ"><Trash2 size={14} /></button></div>
                </div>
              ))}</div>
            )}
          </Panel>
          <Panel title="Public D Analytics & Inquiries">
            {!analytics ? <p className="text-sm text-muted-foreground">No public D activity yet.</p> : (
              <>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-black/30 rounded-lg p-3 text-center"><p className="font-serif text-2xl text-gold">{analytics.conversations}</p><p className="text-[10px] uppercase tracking-label text-muted-foreground">Conversations</p></div>
                  <div className="bg-black/30 rounded-lg p-3 text-center"><p className="font-serif text-2xl text-gold">{analytics.inquiries_total}</p><p className="text-[10px] uppercase tracking-label text-muted-foreground">Inquiries</p></div>
                  <div className="bg-black/30 rounded-lg p-3 text-center"><p className="font-serif text-2xl text-gold">{analytics.follow_up_required}</p><p className="text-[10px] uppercase tracking-label text-muted-foreground">Follow-up</p></div>
                </div>
                {(typeFilter || followupOnly) && (
                  <div className="flex items-center gap-2 mb-3 text-xs" data-testid="inq-filter-chip">
                    <span className="px-2 py-1 rounded-full bg-pool/15 text-pool capitalize">{followupOnly ? "Follow-up required" : `${typeFilter} inquiries`}</span>
                    <button onClick={clearInqFilter} className="text-muted-foreground hover:text-foreground" data-testid="inq-filter-clear">Clear</button>
                  </div>
                )}
                {(() => {
                  const shown = (analytics.inquiries || []).filter((i) => (!typeFilter || i.type === typeFilter) && (!followupOnly || i.follow_up_required));
                  return shown.length === 0 ? <p className="text-sm text-muted-foreground">No inquiries match this filter.</p> : (
                    <div className="space-y-1 max-h-72 overflow-y-auto">{shown.map((i) => (
                      <div key={i.id} className="flex justify-between items-center text-sm border-b border-white/5 py-1.5" data-testid={`pubinq-${i.id}`}>
                        <span>{i.name} · <span className="text-muted-foreground capitalize">{i.type}</span> · <span className="text-xs text-muted-foreground">{i.email}</span>{(i.converted_to || []).length > 0 && <span className="text-[10px] text-gold ml-2">converted</span>}</span>
                        <div className="flex items-center gap-3">
                          <button onClick={() => setConvLead(i)} className="text-xs text-gold" data-testid={`pubinq-convert-${i.id}`}>Convert</button>
                          {i.follow_up_required ? <button onClick={async () => { await api.post(`/ghost/public/inquiries/${i.id}/resolve`); loadPublic(); }} className="text-xs text-pool" data-testid={`pubinq-resolve-${i.id}`}>Mark followed up</button> : <span className="text-xs text-muted-foreground">Followed up</span>}
                        </div>
                      </div>
                    ))}</div>
                  );
                })()}
              </>
            )}
          </Panel>
        </TabsContent>

        <TabsContent value="privacy" className="space-y-6">
          <Panel title="Retention & Data">
            <div className="flex items-center gap-3 mb-4">
              <label className="text-sm">Retention period (days)</label>
              <input type="number" className={adminInput + " w-28"} value={s.retention_days} onChange={(e) => set("retention_days", +e.target.value)} data-testid="ghost-retention" />
            </div>
            <p className="text-xs text-muted-foreground mb-4">Credentials, tokens, full card data and identification are never sent to the AI provider or stored in D logs. Guest content is treated as untrusted data. The provider identity is never shown to guests or staff.</p>
            <button onClick={purge} className="btn-ghost-gold px-4 py-2 text-sm flex items-center gap-2 text-red-300 border-red-400/40" data-testid="ghost-purge"><Trash2 size={14} /> Delete all saved conversations</button>
          </Panel>
        </TabsContent>

        <TabsContent value="audit">
          <Panel title="D Activity Log">
            {logs === null ? <Loading /> : logs.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No D activity recorded yet.</p> : (
              <div className="overflow-x-auto"><table className="w-full min-w-[720px]">
                <thead><tr><Th>Time</Th><Th>User</Th><Th>Role</Th><Th>Audience</Th><Th>Action</Th><Th>Context</Th><Th>Status</Th></tr></thead>
                <tbody>{logs.map((l) => (
                  <tr key={l.id} data-testid={`ghost-log-${l.id}`}>
                    <Td className="text-muted-foreground whitespace-nowrap">{fmtDate(l.created_at)}</Td>
                    <Td className="text-xs">{l.user_email}</Td><Td className="capitalize">{l.role}</Td>
                    <Td className="capitalize">{l.audience}</Td><Td>{l.action}{l.draft_created && <span className="text-[10px] text-pool block">draft</span>}</Td>
                    <Td className="text-muted-foreground">{l.context_type || "—"}</Td>
                    <Td><span className={`status-pill border ${l.provider_status === "configured" ? "text-pool border-pool/40 bg-pool/10" : "text-muted-foreground border-white/20 bg-white/5"}`}>{l.provider_status}</span></Td>
                  </tr>
                ))}</tbody>
              </table></div>
            )}
          </Panel>
        </TabsContent>
      </Tabs>
      <LeadConvertModal lead={convLead} onClose={() => setConvLead(null)} onDone={loadPublic} />
    </div>
  );
}
